/* eslint-disable react/jsx-props-no-spreading */
import { CircularProgress, InputAdornment, Stack } from "@mui/material"
import TextField from "@mui/material/TextField"
import { KeyboardEventHandler, ReactElement } from "react"
import { Control, Controller, UseControllerProps } from "react-hook-form"

import { useScreenSize } from "../../../hooks/useScreenSize"
import { TEXT_FIELD_DEFAULT_PROPS } from "../../../themes/baseTheme"
import { INPUT_HELP_TOOLTIP_SPACING } from "../../../themes/defaultThemeParams"
import HelpIcon from "../../helper/helpIcon"

// Interface for ValidatedTextField
interface IValidatedTextFieldProps {
    control: Control
    required: boolean
    registerName: string
    placeholder: string
    label?: string
    multiline?: boolean
    customRules?: UseControllerProps["rules"]
    loading?: boolean
    tooltip?: null | string
    disabled?: boolean
    handleKeyPress?: KeyboardEventHandler<HTMLDivElement>
    optional?: boolean
}

/**
 * Textfield with designed to be controlled by react-hook-form. Must be used
 * in a component with a useForm hook from react-hook-form. Register name
 * and control are exposed from useForm.
 */
const ValidatedTextField = ({
    control,
    required,
    registerName,
    placeholder,
    multiline,
    label = undefined,
    customRules = undefined,
    loading = false,
    tooltip = null,
    disabled = false,
    handleKeyPress = undefined,
    optional = false,
}: IValidatedTextFieldProps): ReactElement => {
    const requiredRule = {
        required: { value: true, message: "This field is required" },
    }
    const rules = required ? { ...requiredRule, ...customRules } : customRules
    const { isMob } = useScreenSize()

    return (
        <Stack direction="row" spacing={INPUT_HELP_TOOLTIP_SPACING}>
            <Controller
                name={registerName}
                control={control}
                rules={rules}
                defaultValue=""
                render={({ field, fieldState: { error } }) => (
                    <TextField
                        {...field}
                        value={field.value ?? ""}
                        error={!!error}
                        helperText={error?.message}
                        required={required}
                        label={label}
                        placeholder={placeholder}
                        multiline={multiline}
                        disabled={disabled}
                        onChange={(event) => {
                            const { value } = event.target
                            field.onChange(
                                optional && value === "" ? null : value
                            )
                        }}
                        onKeyDown={handleKeyPress}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    {loading && <CircularProgress size={20} />}
                                </InputAdornment>
                            ),
                            ...TEXT_FIELD_DEFAULT_PROPS.InputProps,
                        }}
                    />
                )}
            />
            {tooltip && !isMob && <HelpIcon tooltip={tooltip} />}
        </Stack>
    )
}

ValidatedTextField.defaultProps = {
    loading: false,
    customRules: {},
    multiline: false,
    tooltip: null,
    label: undefined,
    disabled: false,
    handleKeyPress: undefined,
    optional: false,
}

export default ValidatedTextField
